/**
 * Studio-branded monthly PDF report for sharing with clients.
 *
 * Layout (single page when possible):
 *   - Header band with studio logo (left) and report title (right)
 *   - Period range + basis + generation timestamp
 *   - KPI strip: Pianificato / Pagato / Da pagare / Scaduto
 *   - Compact "Da pagare per categoria" table (top 8)
 *   - Compact "Scadenze non saldate" table (top 12, in due-date order)
 *
 * Uses jsPDF + jspdf-autotable already in the project's dependencies — no
 * new heavy deps. Logo is fetched from /api/settings/identity/logo and
 * passed as a data-URL to jsPDF.addImage.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { API } from "@/lib/api-base";

const fmtDateIT = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const fmtEUR = (n) => {
  const num = Number(n || 0);
  const formatted = num.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `€ ${formatted}`;
};

const safeNum = (v) => Number(v || 0);

// Brand colors — Design System Gruppo Impresa Suite v1.0.
const COLOR_PRIMARY = "#0D96BB"; // Turchese Suite — colore modulo Incassi & Pagamenti
const COLOR_TEXT = "#1F2A44"; // near Blu Profondo per corpo testo
const COLOR_SUBTLE = "#6B7280"; // grigio testo secondario
const COLOR_AMBER = "#B45309";
const COLOR_ROSE = "#9F1239";

/**
 * Fetch identity + logo as data URL. Returns {studio_name, logoDataUrl|null}.
 */
async function loadBrand() {
  try {
    const res = await axios.get(`${API}/settings/identity`);
    const identity = res.data || {};
    const studioName = identity.studio_name || "Gestionale Incassi & Pagamenti";
    if (!identity.has_logo) return { studioName, logoDataUrl: null };

    const blob = await axios.get(
      `${API}/settings/identity/logo?v=${identity.logo_version}`,
      { responseType: "blob" },
    );
    const reader = new FileReader();
    const dataUrl = await new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob.data);
    });
    return { studioName, logoDataUrl: dataUrl };
  } catch {
    return { studioName: "Gestionale Incassi & Pagamenti", logoDataUrl: null };
  }
}

/**
 * Build the PDF report.
 *
 * @param {object} args
 * @param {string} args.from  - ISO date_from
 * @param {string} args.to    - ISO date_to
 * @param {string} args.periodLabel - human-readable label (e.g. "Anno 2026")
 * @param {string} args.basis - "cash" | "accrual"
 * @param {Array}  args.occurrences  - in-window occurrences from /api/occurrences
 * @param {Array}  args.expenses     - all expenses (for title fallback)
 * @param {Array}  args.vendors      - all vendors
 * @param {Array}  args.categories   - all categories
 * @param {number} args.paidCash     - sum of payments paid_at in window (cassa)
 */
export async function buildMonthlyPdfReport({
  from,
  to,
  periodLabel,
  basis,
  occurrences,
  expenses,
  vendors,
  categories,
  paidCash,
  arOccurrences = [],
  arCollections = [],
  arClients = [],
  arCategories = [],
}) {
  const { studioName, logoDataUrl } = await loadBrand();

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const todayIso = new Date().toISOString().slice(0, 10);

  /* ---------- Header band ---------- */
  doc.setFillColor(COLOR_PRIMARY);
  doc.rect(0, 0, pageWidth, 28, "F");

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 12, 6, 16, 16);
    } catch {
      /* ignore unsupported image formats */
    }
  }
  doc.setTextColor("#FFFFFF");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(studioName, logoDataUrl ? 32 : 12, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Report del periodo · Esposizione finanziaria", logoDataUrl ? 32 : 12, 19);

  doc.setFontSize(8);
  doc.text(
    `Generato il ${new Date().toLocaleDateString("it-IT")} ${new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`,
    pageWidth - 12,
    19,
    { align: "right" },
  );

  /* ---------- Period bar ---------- */
  let y = 36;
  doc.setTextColor(COLOR_TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(`Periodo: ${periodLabel}`, 12, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(COLOR_SUBTLE);
  doc.text(
    `dal ${fmtDateIT(from)} al ${fmtDateIT(to)} · base ${basis === "cash" ? "cassa" : "competenza"}`,
    pageWidth - 12,
    y,
    { align: "right" },
  );

  /* ---------- KPIs ---------- */
  const contributing = occurrences.filter(
    (r) => r.status !== "cancelled" && r.status !== "skipped",
  );
  const planned = contributing.reduce((s, r) => s + safeNum(r.effective_amount), 0);
  const paidAccrual = contributing.reduce((s, r) => s + safeNum(r.total_paid), 0);
  const paid = basis === "cash" ? paidCash : paidAccrual;
  const remaining = Math.max(0, planned - paid);
  let overdueAmount = 0;
  let overdueCount = 0;
  for (const r of contributing) {
    if (r.status !== "planned" && r.status !== "partially_paid") continue;
    if (!r.due_date || r.due_date >= todayIso) continue;
    overdueAmount += safeNum(r.effective_amount) - safeNum(r.total_paid);
    overdueCount += 1;
  }

  y += 8;
  const kpiWidth = (pageWidth - 24 - 9) / 4; // 12 margin each side, 3 gaps × 3mm
  const kpiHeight = 22;
  const kpis = [
    { label: "Pianificato", value: fmtEUR(planned), color: COLOR_PRIMARY },
    {
      label: basis === "cash" ? "Pagato (cassa)" : "Pagato (competenza)",
      value: fmtEUR(paid),
      color: "#059669",
    },
    { label: "Da pagare", value: fmtEUR(remaining), color: COLOR_AMBER },
    {
      label: `Scaduto (${overdueCount})`,
      value: fmtEUR(overdueAmount),
      color: overdueAmount > 0 ? COLOR_ROSE : COLOR_SUBTLE,
    },
  ];
  kpis.forEach((k, i) => {
    const x = 12 + i * (kpiWidth + 3);
    doc.setDrawColor("#E5DFD5");
    doc.setFillColor("#FDFBF6");
    doc.roundedRect(x, y, kpiWidth, kpiHeight, 2, 2, "FD");
    doc.setTextColor(COLOR_SUBTLE);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(k.label.toUpperCase(), x + 3, y + 6);
    doc.setTextColor(k.color);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(k.value, x + 3, y + 16);
  });

  y += kpiHeight + 8;

  /* ---------- Breakdown by category ---------- */
  const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name]));
  const breakdown = new Map();
  for (const r of contributing) {
    const rem = safeNum(r.effective_amount) - safeNum(r.total_paid);
    if (rem <= 0.005) continue;
    const cid = r.effective_category_id || "__none__";
    const cur = breakdown.get(cid) || { remaining: 0, count: 0 };
    cur.remaining += rem;
    cur.count += 1;
    breakdown.set(cid, cur);
  }
  const breakdownRows = Array.from(breakdown.entries())
    .map(([id, agg]) => [
      catMap[id] || "Senza categoria",
      agg.count,
      fmtEUR(agg.remaining),
    ])
    .sort((a, b) => parseFloat(b[2].replace(/[€.\s]/g, "").replace(",", ".")) -
                    parseFloat(a[2].replace(/[€.\s]/g, "").replace(",", ".")))
    .slice(0, 8);

  if (breakdownRows.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(COLOR_TEXT);
    doc.text("Da pagare per categoria", 12, y);
    y += 3;
    autoTable(doc, {
      startY: y,
      head: [["Categoria", "Voci", "Residuo"]],
      body: breakdownRows,
      theme: "striped",
      headStyles: { fillColor: COLOR_PRIMARY, textColor: "#FFFFFF", fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: COLOR_TEXT },
      alternateRowStyles: { fillColor: "#FAF7EE" },
      columnStyles: {
        1: { halign: "right", cellWidth: 18 },
        2: { halign: "right", cellWidth: 36 },
      },
      margin: { left: 12, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  /* ---------- Unpaid deadlines ---------- */
  const expMap = Object.fromEntries(expenses.map((e) => [e.id, e]));
  const venMap = Object.fromEntries(vendors.map((v) => [v.id, v.name]));
  const unpaid = contributing
    .filter((r) => r.status === "planned" || r.status === "partially_paid")
    .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));

  const unpaidRows = unpaid.slice(0, 12).map((r) => {
    const overdue = r.due_date < todayIso;
    const title = r.effective_title || expMap[r.expense_id]?.title || "—";
    const vendor = venMap[r.effective_vendor_id] || "—";
    const remaining = safeNum(r.effective_amount) - safeNum(r.total_paid);
    return [
      fmtDateIT(r.due_date) + (overdue ? " (scaduta)" : ""),
      title,
      vendor,
      catMap[r.effective_category_id] || "—",
      fmtEUR(remaining),
    ];
  });
  if (unpaidRows.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(COLOR_TEXT);
    doc.text(
      `Scadenze non saldate (prime ${unpaidRows.length} di ${unpaid.length})`,
      12,
      y,
    );
    y += 3;
    autoTable(doc, {
      startY: y,
      head: [["Scadenza", "Descrizione", "Fornitore", "Categoria", "Residuo"]],
      body: unpaidRows,
      theme: "striped",
      headStyles: { fillColor: COLOR_PRIMARY, textColor: "#FFFFFF", fontSize: 9 },
      bodyStyles: { fontSize: 8.5, textColor: COLOR_TEXT },
      alternateRowStyles: { fillColor: "#FAF7EE" },
      columnStyles: {
        0: { cellWidth: 32 },
        4: { halign: "right", cellWidth: 30 },
      },
      margin: { left: 12, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  /* ---------- Page 2: Incassi (Receivables) ---------- */
  const arContributing = arOccurrences.filter(
    (r) => r.status !== "cancelled" && r.status !== "defaulted",
  );
  if (arContributing.length > 0 || arCollections.length > 0) {
    doc.addPage();

    // Header band (compact — same brand)
    doc.setFillColor(COLOR_PRIMARY);
    doc.rect(0, 0, pageWidth, 28, "F");
    if (logoDataUrl) {
      try { doc.addImage(logoDataUrl, 12, 6, 16, 16); } catch { /* noop */ }
    }
    doc.setTextColor("#FFFFFF");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(studioName, logoDataUrl ? 32 : 12, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Report del periodo · Incassi clienti", logoDataUrl ? 32 : 12, 19);

    let yy = 36;
    doc.setTextColor(COLOR_TEXT);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`Periodo: ${periodLabel}`, 12, yy);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(COLOR_SUBTLE);
    doc.text(
      `dal ${fmtDateIT(from)} al ${fmtDateIT(to)} · lato entrate`,
      pageWidth - 12,
      yy,
      { align: "right" },
    );

    // KPI: Previsto / Incassato / Da incassare / Scaduto da incassare
    // Incassato uses cash basis: sum of collections with collected_at in window.
    const arPlanned = arContributing.reduce(
      (s, r) => s + safeNum(r.effective_amount),
      0,
    );
    const arCollected = arCollections.reduce(
      (s, c) => s + safeNum(c.collected_amount),
      0,
    );
    const arRemaining = Math.max(0, arPlanned - arCollected);
    let arOverdue = 0;
    let arOverdueCount = 0;
    for (const r of arContributing) {
      if (r.status !== "due" && r.status !== "partially_collected") continue;
      if (!r.due_date || r.due_date >= todayIso) continue;
      arOverdue += safeNum(r.effective_amount) - safeNum(r.total_collected);
      arOverdueCount += 1;
    }

    yy += 8;
    const arKpis = [
      { label: "Previsto incassi", value: fmtEUR(arPlanned), color: COLOR_PRIMARY },
      { label: "Incassato (cassa)", value: fmtEUR(arCollected), color: "#059669" },
      { label: "Da incassare", value: fmtEUR(arRemaining), color: COLOR_AMBER },
      {
        label: `Scaduto da incassare (${arOverdueCount})`,
        value: fmtEUR(arOverdue),
        color: arOverdue > 0 ? COLOR_ROSE : COLOR_SUBTLE,
      },
    ];
    arKpis.forEach((k, i) => {
      const x = 12 + i * (kpiWidth + 3);
      doc.setDrawColor("#E5DFD5");
      doc.setFillColor("#FDFBF6");
      doc.roundedRect(x, yy, kpiWidth, kpiHeight, 2, 2, "FD");
      doc.setTextColor(COLOR_SUBTLE);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text(k.label.toUpperCase(), x + 3, yy + 6);
      doc.setTextColor(k.color);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text(k.value, x + 3, yy + 16);
    });
    yy += kpiHeight + 8;

    // Net balance summary
    const netExpected = arPlanned - planned;
    const netRealised = arCollected - paidCash;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(COLOR_TEXT);
    doc.text("Saldo netto del periodo", 12, yy);
    yy += 3;
    autoTable(doc, {
      startY: yy,
      head: [["", "Incassi", "Spese", "Saldo netto"]],
      body: [
        ["Previsto", fmtEUR(arPlanned), fmtEUR(planned), (netExpected >= 0 ? "+ " : "- ") + fmtEUR(Math.abs(netExpected))],
        ["Realizzato (cassa)", fmtEUR(arCollected), fmtEUR(paidCash), (netRealised >= 0 ? "+ " : "- ") + fmtEUR(Math.abs(netRealised))],
      ],
      theme: "grid",
      headStyles: { fillColor: COLOR_PRIMARY, textColor: "#FFFFFF", fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: COLOR_TEXT },
      columnStyles: {
        0: { fontStyle: "bold" },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right", fontStyle: "bold" },
      },
      margin: { left: 12, right: 12 },
    });
    yy = doc.lastAutoTable.finalY + 6;

    // Breakdown by client
    const cliMap = Object.fromEntries(arClients.map((c) => [c.id, c.name]));
    const byClient = new Map();
    for (const r of arContributing) {
      const rem = safeNum(r.effective_amount) - safeNum(r.total_collected);
      if (rem <= 0.005) continue;
      const id = r.effective_client_id || "__none__";
      const cur = byClient.get(id) || { remaining: 0, count: 0 };
      cur.remaining += rem;
      cur.count += 1;
      byClient.set(id, cur);
    }
    const clientRows = Array.from(byClient.entries())
      .map(([id, agg]) => [
        cliMap[id] || "Senza cliente",
        agg.count,
        fmtEUR(agg.remaining),
      ])
      .sort(
        (a, b) =>
          parseFloat(b[2].replace(/[€.\s]/g, "").replace(",", ".")) -
          parseFloat(a[2].replace(/[€.\s]/g, "").replace(",", ".")),
      )
      .slice(0, 8);
    if (clientRows.length) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(COLOR_TEXT);
      doc.text("Da incassare per cliente", 12, yy);
      yy += 3;
      autoTable(doc, {
        startY: yy,
        head: [["Cliente", "Voci", "Residuo"]],
        body: clientRows,
        theme: "striped",
        headStyles: { fillColor: COLOR_PRIMARY, textColor: "#FFFFFF", fontSize: 9 },
        bodyStyles: { fontSize: 9, textColor: COLOR_TEXT },
        alternateRowStyles: { fillColor: "#FAF7EE" },
        columnStyles: {
          1: { halign: "right", cellWidth: 18 },
          2: { halign: "right", cellWidth: 36 },
        },
        margin: { left: 12, right: 12 },
      });
      yy = doc.lastAutoTable.finalY + 6;
    }

    // List of uncollected receivables
    const arUncollected = arContributing
      .filter(
        (r) => r.status === "due" || r.status === "partially_collected",
      )
      .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    const arCatMap = Object.fromEntries(arCategories.map((c) => [c.id, c.name]));
    const arRows = arUncollected.slice(0, 12).map((r) => {
      const overdue = r.due_date < todayIso;
      const cli = cliMap[r.effective_client_id] || "—";
      const cat = arCatMap[r.effective_category_id] || "—";
      const remaining = safeNum(r.effective_amount) - safeNum(r.total_collected);
      return [
        fmtDateIT(r.due_date) + (overdue ? " (scaduto)" : ""),
        cli,
        r.invoice_reference || "—",
        cat,
        fmtEUR(remaining),
      ];
    });
    if (arRows.length) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(COLOR_TEXT);
      doc.text(
        `Incassi non riscossi (primi ${arRows.length} di ${arUncollected.length})`,
        12,
        yy,
      );
      yy += 3;
      autoTable(doc, {
        startY: yy,
        head: [["Scadenza", "Cliente", "Rif. fattura", "Categoria", "Residuo"]],
        body: arRows,
        theme: "striped",
        headStyles: { fillColor: COLOR_PRIMARY, textColor: "#FFFFFF", fontSize: 9 },
        bodyStyles: { fontSize: 8.5, textColor: COLOR_TEXT },
        alternateRowStyles: { fillColor: "#FAF7EE" },
        columnStyles: {
          0: { cellWidth: 32 },
          4: { halign: "right", cellWidth: 30 },
        },
        margin: { left: 12, right: 12 },
      });
    }
  }

  /* ---------- Footer ---------- */
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.setTextColor(COLOR_SUBTLE);
    doc.text(
      `${studioName} · Report del ${new Date().toLocaleDateString("it-IT")} · pag. ${i}/${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 6,
      { align: "center" },
    );
  }

  const filename = `report_${from}_${to}.pdf`;
  doc.save(filename);
  return filename;
}


/* ===========================================================================
 * Forecast — annual PDF report
 * ---------------------------------------------------------------------------
 * Single-purpose, professional, printable summary of the Previsione page
 * for the selected year. Includes KPIs, monthly distribution chart (rendered
 * as a native PDF bar chart, no html2canvas), per-month table, per-category
 * table, generated timestamp + footer with page numbering.
 *
 * @param {object} args
 * @param {number}   args.year         - Selected year (e.g. 2026)
 * @param {Array}    args.monthly      - [{year, month, forecast_amount, adjustment_amount, total}, ...]
 * @param {Array}    args.byCategory   - [{category_id, category_name, forecast_amount}, ...]
 * @param {number}   args.yearlyTotal  - Sum of all monthly totals
 * @param {object|null} args.peak      - {month, total} for the highest month (optional)
 * @param {number}   args.monthlyAvg   - Average across non-zero months
 * ===========================================================================
 */
const MESI_IT = [
  "Gen", "Feb", "Mar", "Apr", "Mag", "Giu",
  "Lug", "Ago", "Set", "Ott", "Nov", "Dic",
];

export async function buildForecastPdfReport({
  year,
  monthly = [],
  byCategory = [],
  yearlyTotal = 0,
  peak = null,
  monthlyAvg = 0,
}) {
  const { studioName, logoDataUrl } = await loadBrand();
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const generatedAt = `${new Date().toLocaleDateString("it-IT")} ${new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;

  /* ---------- Header band ---------- */
  doc.setFillColor(COLOR_PRIMARY);
  doc.rect(0, 0, pageWidth, 28, "F");
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 12, 6, 16, 16);
    } catch {
      /* ignore */
    }
  }
  doc.setTextColor("#FFFFFF");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(studioName, logoDataUrl ? 32 : 12, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Previsione spese · Report annuale", logoDataUrl ? 32 : 12, 19);
  doc.setFontSize(8);
  doc.text(`Generato il ${generatedAt}`, pageWidth - 12, 19, { align: "right" });

  /* ---------- Year title ---------- */
  let y = 36;
  doc.setTextColor(COLOR_TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(`Anno ${year}`, 12, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(COLOR_SUBTLE);
  doc.text(
    "Aggregazione per competenza (due_date) delle spese attive e una tantum.",
    pageWidth - 12,
    y,
    { align: "right" },
  );

  /* ---------- KPIs ---------- */
  y += 8;
  const kpiWidth = (pageWidth - 24 - 6) / 3; // 12 margin each side, 2 gaps × 3mm
  const kpiHeight = 22;
  const kpis = [
    { label: "Totale annuo previsto", value: fmtEUR(yearlyTotal), color: COLOR_PRIMARY },
    { label: "Media mensile", value: fmtEUR(monthlyAvg), color: COLOR_TEXT },
    {
      label: peak?.month ? `Picco mensile (${peak.month})` : "Picco mensile",
      value: fmtEUR(peak?.total || 0),
      color: COLOR_AMBER,
    },
  ];
  kpis.forEach((k, i) => {
    const x = 12 + i * (kpiWidth + 3);
    doc.setDrawColor("#E5DFD5");
    doc.setFillColor("#FDFBF6");
    doc.roundedRect(x, y, kpiWidth, kpiHeight, 2, 2, "FD");
    doc.setTextColor(COLOR_SUBTLE);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(k.label.toUpperCase(), x + 3, y + 6);
    doc.setTextColor(k.color);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(k.value, x + 3, y + 16);
  });
  y += kpiHeight + 6;

  /* ---------- Monthly distribution chart (native jsPDF) ---------- */
  doc.setTextColor(COLOR_TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Distribuzione mensile", 12, y);
  y += 4;

  // Build 12 buckets from monthly data
  const buckets = MESI_IT.map((label, idx) => {
    const found = monthly.find(
      (m) => Number(m.month) === idx + 1 && Number(m.year) === Number(year),
    );
    return { label, total: safeNum(found?.total) };
  });
  const maxBar = Math.max(1, ...buckets.map((b) => b.total));
  const peakMonth = peak?.month;

  const chartLeft = 12;
  const chartTop = y;
  const chartRight = pageWidth - 12;
  const chartHeight = 40;
  const chartBottom = chartTop + chartHeight;
  const chartWidth = chartRight - chartLeft;
  const barAreaPad = 2;
  const barSlot = (chartWidth - barAreaPad * 2) / 12;
  const barWidth = barSlot * 0.62;

  // Axis baseline
  doc.setDrawColor("#E7E5E4");
  doc.setLineWidth(0.2);
  doc.line(chartLeft, chartBottom, chartRight, chartBottom);
  // Y-gridlines (3 levels)
  doc.setTextColor(COLOR_SUBTLE);
  doc.setFontSize(6.5);
  for (let g = 1; g <= 3; g++) {
    const yy = chartBottom - (chartHeight * g) / 3;
    doc.line(chartLeft, yy, chartRight, yy);
    doc.text(fmtEUR((maxBar * g) / 3), chartLeft - 1, yy + 1.5, { align: "right" });
  }

  buckets.forEach((b, i) => {
    const x = chartLeft + barAreaPad + i * barSlot + (barSlot - barWidth) / 2;
    const h = (b.total / maxBar) * chartHeight;
    const isPeak = b.label === peakMonth;
    doc.setFillColor(isPeak ? COLOR_AMBER : COLOR_PRIMARY);
    doc.rect(x, chartBottom - h, barWidth, h, "F");
    // Labels
    doc.setTextColor(COLOR_SUBTLE);
    doc.setFontSize(7);
    doc.text(b.label, x + barWidth / 2, chartBottom + 4, { align: "center" });
  });
  y = chartBottom + 10;

  /* ---------- Per-month table ---------- */
  if (y > pageHeight - 70) {
    doc.addPage();
    y = 20;
  }
  doc.setTextColor(COLOR_TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Previsione per mese", 12, y);
  y += 2;

  const monthlyRows = monthly.map((r) => [
    `${MESI_IT[r.month - 1]} ${r.year}`,
    fmtEUR(r.forecast_amount),
    fmtEUR(r.adjustment_amount),
    fmtEUR(r.total),
  ]);
  // Summary footer
  const totForecast = monthly.reduce((s, r) => s + safeNum(r.forecast_amount), 0);
  const totAdj = monthly.reduce((s, r) => s + safeNum(r.adjustment_amount), 0);
  const totAll = monthly.reduce((s, r) => s + safeNum(r.total), 0);

  autoTable(doc, {
    startY: y + 2,
    margin: { left: 12, right: 12 },
    head: [["Mese", "Previsione", "Aggiustamenti", "Totale"]],
    body: monthlyRows,
    foot: [[
      "Totale anno",
      fmtEUR(totForecast),
      fmtEUR(totAdj),
      fmtEUR(totAll),
    ]],
    theme: "grid",
    headStyles: {
      fillColor: COLOR_PRIMARY,
      textColor: "#FFFFFF",
      fontSize: 8,
      fontStyle: "bold",
      halign: "center",
    },
    footStyles: {
      fillColor: "#F5F0E6",
      textColor: COLOR_TEXT,
      fontStyle: "bold",
      fontSize: 8,
    },
    bodyStyles: { fontSize: 8, textColor: COLOR_TEXT },
    columnStyles: {
      0: { cellWidth: 38 },
      1: { halign: "right" },
      2: { halign: "right", textColor: COLOR_SUBTLE },
      3: { halign: "right", fontStyle: "bold" },
    },
    styles: { cellPadding: 1.6, lineColor: "#E7E5E4", lineWidth: 0.15 },
  });
  y = doc.lastAutoTable.finalY + 8;

  /* ---------- Per-category table ---------- */
  if (y > pageHeight - 50) {
    doc.addPage();
    y = 20;
  }
  doc.setTextColor(COLOR_TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Previsione per categoria", 12, y);
  y += 2;

  const totalCat = byCategory.reduce(
    (s, c) => s + safeNum(c.forecast_amount),
    0,
  );
  const catRows = byCategory.map((c) => {
    const amt = safeNum(c.forecast_amount);
    const pct = totalCat > 0 ? (amt / totalCat) * 100 : 0;
    return [c.category_name || "—", fmtEUR(amt), `${pct.toFixed(1)}%`];
  });

  if (catRows.length === 0) {
    catRows.push(["Nessuna categoria con previsione.", "", ""]);
  }

  autoTable(doc, {
    startY: y + 2,
    margin: { left: 12, right: 12 },
    head: [["Categoria", "Importo", "Quota %"]],
    body: catRows,
    foot: catRows.length > 1
      ? [["Totale", fmtEUR(totalCat), "100,0%"]]
      : undefined,
    theme: "grid",
    headStyles: {
      fillColor: COLOR_PRIMARY,
      textColor: "#FFFFFF",
      fontSize: 8,
      fontStyle: "bold",
      halign: "center",
    },
    footStyles: {
      fillColor: "#F5F0E6",
      textColor: COLOR_TEXT,
      fontStyle: "bold",
      fontSize: 8,
    },
    bodyStyles: { fontSize: 8, textColor: COLOR_TEXT },
    columnStyles: {
      1: { halign: "right", fontStyle: "bold" },
      2: { halign: "right", textColor: COLOR_SUBTLE },
    },
    styles: { cellPadding: 1.6, lineColor: "#E7E5E4", lineWidth: 0.15 },
  });

  /* ---------- Footer with page numbering ---------- */
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.setTextColor(COLOR_SUBTLE);
    doc.text(
      `${studioName} · Previsione ${year} · pag. ${i}/${pageCount} · generato il ${generatedAt}`,
      pageWidth / 2,
      pageHeight - 6,
      { align: "center" },
    );
  }

  const filename = `previsione_${year}.pdf`;
  doc.save(filename);
  return filename;
}
